import DashboardIcon from "@mui/icons-material/Dashboard";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FreeBreakfastOutlinedIcon from "@mui/icons-material/FreeBreakfastOutlined";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import WorkOutlineIcon from "@mui/icons-material/WorkOutlineOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DateTime } from "luxon";
import { type FC, type ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { type SideRailItem, SystemSideRail } from "../../components/UIToolkit/Navigation/SystemSideRail/SystemSideRail";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../contexts/ChosenDepartment";
import { ALPHA, ICON_TILE, SURFACE } from "../../theme/tokens";
import { AuditTab } from "./AuditTab/AuditTab";
import { BreaksTab } from "./BreaksTab/BreaksTab";
import { currentFyQuarterToDateRange } from "./fyQuarter";
import { useKeepMountedOnceActive } from "./hooks/useKeepMountedOnceActive";
import { usePbjModelDraft } from "./hooks/usePbjModelDraft";
import { useTabSearchParam } from "./hooks/useTabSearchParam";
import { JobsTab } from "./JobsTab/JobsTab";
import { PeriodControlsPopover } from "./PeriodControlsPopover";
import { PlanTab } from "./PlanTab/PlanTab";
import { PredictionTab } from "./PredictionTab/PredictionTab";
import { SubmissionSheetsTab } from "./SubmissionSheetsTab/SubmissionSheetsTab";

type PbjModelTab = "prediction" | "plan" | "audit" | "submission" | "jobs" | "breaks";

const TAB_PARAM = "tab";
const VALID_TABS: PbjModelTab[] = ["prediction", "plan", "audit", "submission", "jobs", "breaks"];
/** Tabs that are a single full-height table: they fill the content pane and scroll on the grid. */
const PANE_FILLING_TABS: PbjModelTab[] = ["submission", "jobs"];
/**
 * Floor for a pane-filling tab. Filling the viewport is only an improvement while the viewport is tall
 * enough for a usable table — below this the tab keeps its height and the pane scrolls again, instead of
 * crushing the grid into a few rows because the surrounding chrome ate the space.
 */
const PANE_FILLING_MIN_HEIGHT_PX = 700;

type Range = { start: DateTime; end: DateTime };

const initialRange = (): Range => currentFyQuarterToDateRange();

// Empty string defeats `enabled` on the query hooks until the page commits a range.
const toIsoOrEmpty = (dt: DateTime): string => dt.toISODate() ?? "";

export const PbjModelPage: FC = () => {
	const { t } = useTranslation();
	const { track } = useAnalytics();
	// Single source of truth for the active dept: the global header switcher (kept in
	// `chosenDepartment`) drives every per-dept query on this page. No local picker.
	const { chosenDepartment } = useChosenDepartment();
	const selectedDeptId = chosenDepartment.department_id;

	// Active tab is backed by the `?tab=` search param (invalid/missing → prediction);
	// the setter replaces the history entry so tab switches don't stack up in back-history.
	const [tab, setTab] = useTabSearchParam<PbjModelTab>(TAB_PARAM, VALID_TABS, "prediction");

	// Date range is page-session state (not persisted on the row). Default to the
	// current FY quarter through today so admins land on the window they're
	// actively accruing toward.
	const [range, setRange] = useState<Range>(initialRange);

	// Committed snapshot for the prediction/audit query keys. Only the Calculate
	// button advances this — live edits never refetch on their own.
	const [committed, setCommitted] = useState<Range>(initialRange);

	// A successful calculate commits the currently-edited window into the query keys.
	const commitRange = useCallback(() => setCommitted(range), [range]);

	// The editing buffer (saved row, live draft, dirty check, persist+recompute) lives in the
	// hook so the dirty check is a schema-keyed diff that can't fall behind new fields.
	const { savedRow, configDraft, setConfigDraft, isDirty, runCalculate, isCalculating } = usePbjModelDraft({
		deptId: selectedDeptId,
		savedModel: chosenDepartment.pbjModel,
		onCommitted: commitRange,
	});

	useEffect(() => {
		track("pbj_model_tab_viewed", { tab, department_id: selectedDeptId });
	}, [tab, track, selectedDeptId]);

	// Mount-once latches: each heavy tab is created lazily on first visit, then kept
	// mounted (via `display: none`) so its state/scroll/queries survive a tab switch.
	// The tracker reads the PLAN's own quarter, not the page's committed range, so it mounts lazily
	// on first visit like the other secondary tabs and never refetches on a range change.
	const planMounted = useKeepMountedOnceActive(tab === "plan");
	const auditMounted = useKeepMountedOnceActive(tab === "audit");
	const submissionMounted = useKeepMountedOnceActive(tab === "submission");
	const jobsMounted = useKeepMountedOnceActive(tab === "jobs");
	const breaksMounted = useKeepMountedOnceActive(tab === "breaks");

	const handleCalculate = useCallback(() => runCalculate(configDraft), [runCalculate, configDraft]);

	const railItems: SideRailItem<PbjModelTab>[] = useMemo(
		() => [
			{ value: "prediction", label: t("pbj_model.tabs.prediction"), icon: <DashboardIcon /> },
			{ value: "plan", label: t("pbj_model.tabs.plan"), icon: <TrackChangesIcon /> },
			{ value: "audit", label: t("pbj_model.tabs.audit"), icon: <ManageSearchIcon /> },
			{ value: "submission", label: t("pbj_model.tabs.submission"), icon: <DescriptionOutlinedIcon /> },
			{ value: "jobs", label: t("pbj_model.tabs.jobs"), icon: <WorkOutlineIcon /> },
			{ value: "breaks", label: t("pbj_model.tabs.breaks"), icon: <FreeBreakfastOutlinedIcon /> },
		],
		[t],
	);

	// The form-y top bar is gone: the period + facility/state controls + Calculate now live
	// in a "Period & facility" popover. `renderChangePeriod` wraps a caller-supplied trigger (the
	// hero's context-row affordance, or a header button) in that popover — the shared period+facility
	// editor is reachable on *every* tab, restoring main's global-bar behavior. On the dept-config
	// tabs (Jobs/Breaks) the date controls are simply inert (no date-scoped data there), but the
	// facility identity + Calculate still apply, so those tabs get the affordance too.
	const renderChangePeriod = useCallback(
		(trigger: ReactElement) => (
			<PeriodControlsPopover
				range={range}
				onRangeChange={setRange}
				configDraft={configDraft}
				onConfigDraftChange={setConfigDraft}
				onCalculate={handleCalculate}
				isCalculating={isCalculating}
				isDirty={isDirty}
			>
				{trigger}
			</PeriodControlsPopover>
		),
		[range, configDraft, setConfigDraft, handleCalculate, isCalculating, isDirty],
	);

	return (
		<Box data-testid="pbj-model-page" sx={{ display: "flex", width: "100%", height: "100%", minHeight: 0, bgcolor: SURFACE }}>
			<SystemSideRail<PbjModelTab>
				dataTestId="pbj-model-rail"
				ariaLabel={t("pbj_model.title")}
				items={railItems}
				activeValue={tab}
				onChange={setTab}
				sx={{ overflowY: "auto" }}
				header={
					<Stack direction="row" spacing={1.25} sx={{ alignItems: "center", minWidth: 0 }}>
						<Box
							sx={(theme) => ({
								flexShrink: 0,
								width: ICON_TILE.SM.size,
								height: ICON_TILE.SM.size,
								bgcolor: alpha(theme.palette.primary.main, ALPHA.BRAND_TINT),
								color: "primary.main",
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							})}
						>
							<MonitorHeartIcon sx={{ fontSize: ICON_TILE.SM.glyph }} />
						</Box>
						<Box sx={{ minWidth: 0 }}>
							{/* The page's single h1 (a11y): the persistent PBJ Model identity in the rail, always
							    mounted + unique. Every tab's SystemPageHeader defaults to h2, so no h1s collide. */}
							<Typography component="h1" variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
								{t("pbj_model.rail.heading")}
							</Typography>
							<Typography variant="caption" color="text.secondary" noWrap>
								{chosenDepartment.display_name}
							</Typography>
						</Box>
					</Stack>
				}
			/>

			{/* Content pane — its own scroll container so the rail stays put. Full-width (no
			    maxWidth container) so the page reads as a workspace, not a centered document.
			    The form-y top bar is gone; each surface owns its own header (the dark hero on
			    Prediction, a SystemPageHeader on the data tabs). */}
			{/* Content canvas carries the faint brand wash on every tab (it started on Breaks): each tab's
			    white cards lift off a full-bleed tint via their shadow, so the surfaces read as cards on a
			    workspace rather than white-on-white. The rail stays on the plain surface. */}
			<Box
				sx={{
					flexGrow: 1,
					minWidth: 0,
					height: "100%",
					overflow: "auto",
					position: "relative",
					bgcolor: "background.default",
				}}
				data-testid="pbj-model-content"
			>
				{/* Grid-first tabs fill the pane exactly (definite `height: 100%` → their table owns the
				    scroll, no page scroller); the content-sized tabs stay `auto` so they keep growing and
				    scroll the pane. */}
				<Box
					sx={{
						px: { xs: 2, md: 3 },
						py: 3,
						boxSizing: "border-box",
						display: "flex",
						flexDirection: "column",
						height: PANE_FILLING_TABS.includes(tab) ? "100%" : "auto",
					}}
				>
					<Box sx={{ display: tab === "prediction" ? "block" : "none" }}>
						<PredictionTab
							committedStart={toIsoOrEmpty(committed.start)}
							committedEnd={toIsoOrEmpty(committed.end)}
							configDraft={configDraft}
							onConfigDraftChange={setConfigDraft}
							onCalculate={runCalculate}
							isCalculating={isCalculating}
							renderChangePeriod={renderChangePeriod}
							savedCmi={{ pccPdpmAvg: savedRow.pccPdpmAvg, natNursingCaseMixIndex: savedRow.natNursingCaseMixIndex }}
							onPlanTracked={() => setTab("plan")}
						/>
					</Box>
					{planMounted && (
						<Box sx={{ display: tab === "plan" ? "block" : "none" }}>
							<PlanTab onEditPlan={() => setTab("prediction")} />
						</Box>
					)}
					{auditMounted && (
						<Box sx={{ display: tab === "audit" ? "block" : "none" }}>
							<AuditTab
								committedStart={toIsoOrEmpty(committed.start)}
								committedEnd={toIsoOrEmpty(committed.end)}
								savedModel={savedRow}
								isCalculating={isCalculating}
								renderChangePeriod={renderChangePeriod}
							/>
						</Box>
					)}
					{submissionMounted && (
						<Box sx={{ display: tab === "submission" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: PANE_FILLING_MIN_HEIGHT_PX }}>
							<SubmissionSheetsTab
								committedStart={toIsoOrEmpty(committed.start)}
								committedEnd={toIsoOrEmpty(committed.end)}
								isCalculating={isCalculating}
								renderChangePeriod={renderChangePeriod}
							/>
						</Box>
					)}
					{jobsMounted && (
						<Box sx={{ display: tab === "jobs" ? "flex" : "none", flexDirection: "column", flex: 1, minHeight: PANE_FILLING_MIN_HEIGHT_PX }}>
							<JobsTab renderChangePeriod={renderChangePeriod} />
						</Box>
					)}
					{breaksMounted && (
						<Box sx={{ display: tab === "breaks" ? "block" : "none" }}>
							<BreaksTab renderChangePeriod={renderChangePeriod} />
						</Box>
					)}
				</Box>
			</Box>
		</Box>
	);
};
